
import { useState } from "react";
import { User } from "@/lib/types";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";

interface DeleteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedUser: User | null;
  onDeleteUser: (userId: string) => Promise<void>;
  isPending: boolean;
}

export function DeleteUserDialog({
  open,
  onOpenChange,
  selectedUser,
  onDeleteUser,
  isPending
}: DeleteUserDialogProps) {
  const [confirmText, setConfirmText] = useState("");
  const isConfirmationValid = confirmText === selectedUser?.email;

  const handleDelete = async () => {
    if (!selectedUser || !isConfirmationValid) return;
    
    await onDeleteUser(selectedUser.id);
    setConfirmText("");
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-destructive flex items-center gap-2">
            <Trash2 className="h-5 w-5" />
            Delete User
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-4">
            <p>
              Are you sure you want to delete the user <strong>{selectedUser?.email}</strong>?
              This action is irreversible and will delete:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>User account and authentication data</li>
              <li>All roles and permissions</li>
              <li>All notifications</li>
              <li>All contracts and related data</li>
              <li>All categories, currencies, and contacts</li>
              <li>All bug reports</li>
              <li>Dashboard layouts and preferences</li>
            </ul>
            <div className="pt-2">
              <p className="mb-2 font-medium">Type <strong>{selectedUser?.email}</strong> to confirm:</p>
              <input
                type="text"
                className="w-full border border-input p-2 rounded-md"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={selectedUser?.email}
              />
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={!isConfirmationValid || isPending}
            className="bg-destructive hover:bg-destructive/90"
          >
            {isPending ? "Deleting..." : "Delete User"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
